import { Icon } from "@iconify/react";
import { CircularProgress } from "@mui/material";
import {
  CellClassParams,
  ColDef,
  GridApi,
  ValueGetterParams,
  ValueSetterParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { FileDropzone } from "../../components/shared/FileDropzone";
import { Instructions } from "../../components/shared/Instructions";
import {
  ButtonPrimary,
  ButtonPrimary1,
  CellRenderer,
  HStack,
} from "../../components/utils";
import {
  useDownloadHistoricExerciseTemplate,
  useExerciseEmployeeMap,
  useHistoricalExercise,
  useUploadExerciseLetters,
} from "../../queries/exercise";
import {
  ExerciseLetterUrl,
  ExerciseValidationField,
  HistoricExerciseImportReq,
  HistoricExerciseImportRes,
  HistoricExerciseValidateRes,
  UploadExerciseLetter,
} from "../../types/Exercise";

import { downloadBlobObject } from "../../utils/DownloadFile";
import convertToBase64 from "../../utils/convertToBase64";

const EXERCISE_LETTER_COL = "Exercise Letter URL";

function isExerciseLetterUrl(v: unknown): v is ExerciseLetterUrl {
  return (
    typeof v === "object" &&
    v !== null &&
    "url" in v &&
    "s3Key" in (v as Record<string, unknown>)
  );
}

function ExerciseLinkCellRenderer(props: {
  value: ExerciseValidationField["value"];
  valid: boolean;
  error: string | null;
}) {
  if (!props.valid) {
    return (
      <>
        <p>
          <span>
            {isExerciseLetterUrl(props.value) ? "" : String(props.value ?? "")}
          </span>
        </p>
        {props.error && (
          <div className="mt-3.5">
            {props.error.split("\n").map((line, i) => (
              <div key={i} className="leading-none py-0">
                <span className="italic text-xxs">* {line}</span>
              </div>
            ))}
          </div>
        )}
      </>
    );
  }
  if (isExerciseLetterUrl(props.value)) {
    return (
      <a
        href={props.value.url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-orange-500 underline hover:text-orange-600 text-sm"
      >
        Exercise Letter
      </a>
    );
  }
  return <span>{props.value != null ? String(props.value) : ""}</span>;
}

// Excel stores dates as serial numbers (days since Jan 0 1900 with leap year bug).
// 25569 = days between Jan 0 1900 and Jan 1 1970 in Excel's model.
function excelSerialToDisplayDate(serial: number): string {
  const date = new Date(Math.round((serial - 25569) * 86400 * 1000));
  const dd = String(date.getUTCDate()).padStart(2, "0");
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const yyyy = date.getUTCFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

// Handles both numeric (45658) and string ("45658") serial dates from the API.
function normaliseField(
  field: ExerciseValidationField
): ExerciseValidationField {
  if (!field.name.toLowerCase().includes("date")) return field;
  if (isExerciseLetterUrl(field.value)) return field;
  const num =
    typeof field.value === "number" ? field.value : Number(field.value);
  if (!Number.isNaN(num) && num > 25569 && num < 73050) {
    return { ...field, value: excelSerialToDisplayDate(num) };
  }
  return field;
}

function HistoricExerciseImport() {
  const navigate = useNavigate();
  const [file, setFile] = useState<File>();
  const [validationResult, setValidationResult] =
    useState<HistoricExerciseValidateRes | null>(null);
  const [editableEvent, setEditableEvent] = useState<ExerciseValidationField[]>(
    []
  );
  const offersGridRef = useRef<GridApi | null>(null);
  const bulkInputRef = useRef<HTMLInputElement>(null);
  const [uploadedLetters, setUploadedLetters] = useState<
    Record<string, { file: File; base64: string }>
  >({});

  const { refetch: refetchTemplate } = useDownloadHistoricExerciseTemplate();
  const { mutate: callHistoricalExercise, isLoading: isCallingApi } =
    useHistoricalExercise();
  const { mutate: uploadLetters, isLoading: isUploadingLetters } =
    useUploadExerciseLetters();
  const hasValidationResult = validationResult !== null;
  const { data: employeeMap, isLoading: isLoadingEmployees } =
    useExerciseEmployeeMap(hasValidationResult);
  const [hasGridErrors, setHasGridErrors] = useState(false);
  const [isBackendValid, setIsBackendValid] = useState(false);

  useEffect(() => {
    if (validationResult?.event) {
      setEditableEvent(validationResult.event.map(normaliseField));
    }
    if (validationResult?.offers) {
      const anyErrors = validationResult.offers.some((row) =>
        row.some((f) => !f.valid)
      );
      setHasGridErrors(anyErrors);
    } else {
      setHasGridErrors(false);
    }
    setIsBackendValid(validationResult?.valid === true);
  }, [validationResult]);

  async function downloadTemplate() {
    const template = await refetchTemplate();
    const raw = template.data?.data.base64Template ?? "";
    const base64 = raw.includes(",") ? raw.split(",")[1] : raw;
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    const blob = new Blob([bytes], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    downloadBlobObject(blob, "historic-exercise-template.xlsx");
  }

  async function handleFileSelect(selectedFile: File) {
    setFile(selectedFile);
    setValidationResult(null);
    const base64Full = (await convertToBase64(selectedFile)) as string;
    const content = base64Full.includes(",")
      ? base64Full.split(",")[1]
      : base64Full;
    callHistoricalExercise(
      { file: content },
      {
        onSuccess: (data) => {
          setValidationResult(
            data.data as unknown as HistoricExerciseValidateRes
          );
        },
        onError: (err) => {
          const axiosErr = err as {
            response?: { data?: { reason?: string } };
          };
          toast(axiosErr?.response?.data?.reason || "Validation failed", {
            type: "error",
            autoClose: 2000,
          });
        },
      }
    );
  }

  function handleValidate() {
    const correctedOffers: ExerciseValidationField[][] = [];
    offersGridRef.current?.forEachNode((node) => {
      correctedOffers.push(node.data as ExerciseValidationField[]);
    });
    callHistoricalExercise(
      { correctedEvent: editableEvent, correctedOffers },
      {
        onSuccess: (data) => {
          setValidationResult(
            data.data as unknown as HistoricExerciseValidateRes
          );
        },
        onError: (err) => {
          const axiosErr = err as {
            response?: { data?: { reason?: string } };
          };
          toast(axiosErr?.response?.data?.reason || "Validation failed", {
            type: "error",
            autoClose: 2000,
          });
        },
      }
    );
  }

  function handleClear() {
    setFile(undefined);
    setValidationResult(null);
    setEditableEvent([]);
    setUploadedLetters({});
    setHasGridErrors(false);
    setIsBackendValid(false);
  }

  function handleEventFieldChange(index: number, newValue: string) {
    setIsBackendValid(false);
    setEditableEvent((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        value: newValue,
        valid: true,
        error: null,
      };
      return updated;
    });
  }

  async function handleLetterUpload(hrId: string, f: File) {
    const base64 = (await convertToBase64(f)) as string;
    const content = base64.includes(",") ? base64.split(",")[1] : base64;
    setUploadedLetters((prev) => ({
      ...prev,
      [hrId]: { file: f, base64: content },
    }));
  }

  function handleLetterRemove(hrId: string) {
    setUploadedLetters((prev) => {
      const { [hrId]: _removed, ...rest } = prev;
      return rest;
    });
  }

  function fuzzyMatch(filename: string, hrIds: string[]): string | null {
    const normalized = filename.toLowerCase().replace(/[\s\-_]+/g, "");
    // Exact match first
    const exact = hrIds.find(
      (id) => id.toLowerCase() === filename.toLowerCase()
    );
    if (exact) return exact;
    // Normalized match (ignore spaces, hyphens, underscores)
    const normalizedMatch = hrIds.find(
      (id) => id.toLowerCase().replace(/[\s\-_]+/g, "") === normalized
    );
    if (normalizedMatch) return normalizedMatch;
    // Substring match — filename contains HR ID or vice versa
    const containsMatch = hrIds.find(
      (id) =>
        normalized.includes(id.toLowerCase().replace(/[\s\-_]+/g, "")) ||
        id
          .toLowerCase()
          .replace(/[\s\-_]+/g, "")
          .includes(normalized)
    );
    if (containsMatch) return containsMatch;
    return null;
  }

  async function handleBulkSelect(files: FileList) {
    const hrIds = employeesForLetters.map((e) => e.hrId);
    const unmatched: string[] = [];
    const updates: Record<string, { file: File; base64: string }> = {};
    await Promise.all(
      Array.from(files).map(async (f) => {
        const nameWithoutExt = f.name.replace(/\.[^/.]+$/, "");
        const matchedHrId = fuzzyMatch(nameWithoutExt, hrIds);
        if (matchedHrId) {
          const base64 = (await convertToBase64(f)) as string;
          const content = base64.includes(",") ? base64.split(",")[1] : base64;
          updates[matchedHrId] = { file: f, base64: content };
        } else {
          unmatched.push(f.name);
        }
      })
    );
    setUploadedLetters((prev) => ({ ...prev, ...updates }));
    const matched = Object.keys(updates).length;
    if (matched > 0) {
      toast(`${matched} file${matched !== 1 ? "s" : ""} matched and ready.`, {
        type: "success",
        autoClose: 2000,
      });
    }
    if (unmatched.length > 0) {
      toast(
        `${unmatched.length} file${
          unmatched.length !== 1 ? "s" : ""
        } could not be matched to any HR ID: ${unmatched.join(", ")}`,
        { type: "warning", autoClose: 5000 }
      );
    }
  }

  function handleConfirm() {
    const correctedOffers: ExerciseValidationField[][] = [];
    offersGridRef.current?.forEachNode((node) => {
      correctedOffers.push(node.data as ExerciseValidationField[]);
    });
    const req: HistoricExerciseImportReq = {
      correctedEvent: editableEvent,
      correctedOffers,
      confirm: true,
    };
    callHistoricalExercise(req, {
      onSuccess: (data) => {
        const txId = (data.data as unknown as HistoricExerciseImportRes)
          .transactionId;
        const letterEntries = Object.entries(uploadedLetters);
        if (letterEntries.length === 0 || !employeeMap) {
          toast("Import successful", { type: "success", autoClose: 2000 });
          navigate("/options/esopTransactions");
          return;
        }
        const letters: UploadExerciseLetter[] = [];
        for (const [hrId, { base64, file: letterFile }] of letterEntries) {
          const employeeId = employeeMap.get(hrId);
          if (!employeeId) {
            toast(`Could not resolve employee ID for HR ID: ${hrId}`, {
              type: "error",
              autoClose: 3000,
            });
            return;
          }
          const ext = letterFile.name.split(".").pop()?.toLowerCase();
          const fileType: "pdf" | "docx" = ext === "pdf" ? "pdf" : "docx";
          letters.push({ employeeId, file: base64, fileType });
        }
        uploadLetters(
          { transactionId: txId, letters },
          {
            onSuccess: (uploadData) => {
              const successCount = uploadData.data?.uploaded?.length || 0;
              const failCount = uploadData.data?.failed?.length || 0;
              if (failCount === 0) {
                toast(
                  `Import successful! ${successCount} letter(s) uploaded.`,
                  { type: "success", autoClose: 2000 }
                );
              } else {
                toast(
                  `Import successful. ${successCount} uploaded, ${failCount} failed.`,
                  {
                    type: "warning",
                    autoClose: 3000,
                  }
                );
              }
              navigate("/options/esopTransactions");
            },
            onError: (err: unknown) => {
              const axiosErr = err as {
                response?: { data?: { reason?: string } };
              };
              toast(
                axiosErr?.response?.data?.reason || "Letter upload failed",
                {
                  type: "error",
                  autoClose: 2000,
                }
              );
            },
          }
        );
      },
      onError: (err: unknown) => {
        const axiosErr = err as { response?: { data?: { reason?: string } } };
        toast(axiosErr?.response?.data?.reason || "Import failed", {
          type: "error",
          autoClose: 2000,
        });
      },
    });
  }

  // One unique entry per employee, keyed by HR ID (deduped by Employee HR ID field).
  const employeesForLetters = useMemo<
    { hrId: string; displayName: string; grantsCount: number }[]
  >(() => {
    if (!validationResult?.offers?.length) return [];
    const seen = new Map<
      string,
      { displayName: string; grantsCount: number }
    >();
    for (const row of validationResult.offers) {
      const hrIdField = row.find((f) => /hr.?id/i.test(f.name));
      const nameField = row.find((f) =>
        /stakeholder|employee.?name/i.test(f.name)
      );
      const hrId = hrIdField?.value != null ? String(hrIdField.value) : null;
      if (hrId) {
        if (seen.has(hrId)) {
          seen.get(hrId)!.grantsCount += 1;
        } else {
          seen.set(hrId, {
            displayName:
              nameField?.value != null
                ? String(nameField.value)
                : `Employee ${seen.size + 1}`,
            grantsCount: 1,
          });
        }
      }
    }
    return Array.from(seen.entries()).map(([hrId, v]) => ({
      hrId,
      displayName: v.displayName,
      grantsCount: v.grantsCount,
    }));
  }, [validationResult]);

  const offerColumnNames = useMemo<string[]>(() => {
    if (
      !validationResult?.offers?.length ||
      !validationResult.offers[0]?.length
    )
      return [];
    // Exclude Exercise Letter URL — it is per-employee and shown in its own section.
    return validationResult.offers[0]
      .filter((f) => f.name !== EXERCISE_LETTER_COL)
      .map((f) => f.name);
  }, [validationResult]);

  function offerValueGetter(
    params: ValueGetterParams<ExerciseValidationField[]>
  ): string {
    const colId = params.column.getColId();
    const field = params.data?.find((item) => item.name === colId);
    if (!field) return "";
    if (isExerciseLetterUrl(field.value)) {
      return field.valid ? "Exercise Letter" : "";
    }
    const normalised = normaliseField(field);
    return normalised.value != null ? String(normalised.value) : "";
  }

  function offerValueSetter(params: ValueSetterParams): boolean {
    const colId = params.colDef.field;
    if (!colId) return false;
    const rowData = params.data as ExerciseValidationField[];
    const fieldIndex = rowData.findIndex((f) => f.name === colId);
    if (fieldIndex === -1) return false;
    rowData[fieldIndex] = {
      ...rowData[fieldIndex],
      value: params.newValue,
      valid: true,
      error: null,
    };
    params.node?.setData([...rowData]);
    offersGridRef.current?.refreshCells();
    const allData: ExerciseValidationField[][] = [];
    offersGridRef.current?.forEachNode((node) => {
      allData.push(node.data as ExerciseValidationField[]);
    });
    setHasGridErrors(allData.some((row) => row.some((f) => !f.valid)));
    setIsBackendValid(false);
    return true;
  }

  function offerCellStyle(
    params: CellClassParams<ExerciseValidationField[], string>
  ) {
    const colId = params.column.getColId();
    const field = params.data?.find((item) => item.name === colId);
    return field?.valid !== false
      ? { color: "black", backgroundColor: "unset" }
      : { color: "red", backgroundColor: "unset" };
  }

  const offerColumnDefs = useMemo<ColDef[]>(
    () =>
      offerColumnNames.map((colName) => ({
        headerName: colName,
        field: colName,
        valueGetter: offerValueGetter,
        valueSetter:
          colName !== EXERCISE_LETTER_COL ? offerValueSetter : undefined,
        editable: colName !== EXERCISE_LETTER_COL,
        cellRenderer:
          colName === EXERCISE_LETTER_COL
            ? ExerciseLinkCellRenderer
            : CellRenderer,
        cellRendererParams: (params: {
          data: ExerciseValidationField[];
          value: string;
        }) => {
          if (colName === EXERCISE_LETTER_COL) {
            const field = params.data?.find((f) => f.name === colName);
            return {
              value: field?.value ?? null,
              valid: field?.valid ?? true,
              error: field?.error ?? null,
            };
          }
          const field = params.data?.find((f) => f.name === colName);
          const normalised = field
            ? normaliseField(field)
            : { name: colName, value: params.value, valid: true, error: null };
          return normalised;
        },
        cellStyle: offerCellStyle,
        minWidth: 160,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        autoHeight: true,
        resizable: true,
        headerClass: "text-sm font-normal capitalize",
      })),
    [offerColumnNames]
  );

  const eventHasErrors = editableEvent.some((f) => !f.valid);
  const hasAnyError = eventHasErrors || hasGridErrors;
  const isPreviewMode = hasValidationResult && isBackendValid;

  const grantsCount = validationResult?.offers?.length ?? 0;
  const uniqueEmployeesCount = employeesForLetters.length;
  const isValidating = isCallingApi;
  const isConfirming = isCallingApi || isUploadingLetters;

  return (
    <div className="w-full h-full bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col min-h-[600px]">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">
            Import Historic Exercise Event
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Upload an Excel file to import historic exercise event data.
          </p>
        </div>
        <ButtonPrimary
          className="flex items-center gap-2"
          onClick={downloadTemplate}
        >
          <Icon icon="mdi:download-outline" height={18} />
          Download Template
        </ButtonPrimary>
      </div>

      <div className="flex-1 flex flex-col p-6 gap-6">
        {/* File upload */}
        <div className="w-full max-w-2xl mx-auto">
          <FileDropzone
            onFileSelect={handleFileSelect}
            selectedFile={file}
            onClear={handleClear}
            disabled={isCallingApi || isUploadingLetters}
          />
        </div>

        {/* Spinner */}
        {isValidating && (
          <div className="flex flex-col items-center gap-3 py-4">
            <CircularProgress size={36} />
            <p className="text-sm text-gray-500">Validating file...</p>
          </div>
        )}

        {/* Validation results */}
        {!isValidating && hasValidationResult && (
          <>
            {/* Overall status banner */}
            {hasAnyError ? (
              <div className="flex items-center gap-2 p-3 border border-amber-200 bg-amber-50 rounded-md text-amber-800">
                <Icon icon="material-symbols:warning-outline" height={20} />
                <span className="text-sm font-medium">
                  Some fields have errors. Correct the highlighted fields and
                  click Validate.
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2 p-3 border border-green-200 bg-green-50 rounded-md text-green-800">
                <Icon
                  icon="material-symbols:check-circle-outline"
                  height={20}
                />
                <span className="text-sm font-medium">
                  All fields are valid. Click Confirm to create the exercise
                  event.
                </span>
              </div>
            )}

            {/* Event details table */}
            {editableEvent.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    Event Details
                  </h3>
                </div>

                <div className="border border-gray-200 rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left px-4 py-2.5 font-medium text-gray-600 w-1/3">
                          Field
                        </th>
                        <th className="text-left px-4 py-2.5 font-medium text-gray-600">
                          Value
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {editableEvent.map((field, idx) => (
                        <tr
                          key={idx}
                          className={`border-b border-gray-100 last:border-0 ${
                            !field.valid ? "bg-red-50" : ""
                          }`}
                        >
                          <td className="px-4 py-2 font-medium text-gray-700 align-middle">
                            {field.name}
                          </td>
                          <td className="px-4 py-1.5">
                            <input
                              type="text"
                              className={`w-full rounded border px-2 py-1 text-sm focus:outline-none focus:ring-1 ${
                                !field.valid
                                  ? "border-red-400 bg-red-50 text-red-700 focus:ring-red-400"
                                  : "border-gray-200 bg-white text-gray-800 focus:ring-orange-400"
                              }`}
                              value={
                                field.value != null ? String(field.value) : ""
                              }
                              onChange={(e) =>
                                handleEventFieldChange(idx, e.target.value)
                              }
                            />
                            {!field.valid && field.error && (
                              <p className="text-xs text-red-500 mt-0.5">
                                {field.error}
                              </p>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Offers grid */}
            {validationResult.offers.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    Exercise Transactions
                  </h3>
                  <span className="text-xs text-gray-400">
                    {grantsCount} grant{grantsCount !== 1 ? "s" : ""} across{" "}
                    {uniqueEmployeesCount} employee
                    {uniqueEmployeesCount !== 1 ? "s" : ""}
                  </span>
                </div>

                <div
                  className="ag-theme-material border border-gray-200 rounded-md overflow-hidden"
                  style={{
                    height: Math.min(
                      520,
                      80 + validationResult.offers.length * 52
                    ),
                  }}
                >
                  <AgGridReact
                    key="HISTORIC_EXERCISE_OFFERS_GRID"
                    columnDefs={offerColumnDefs}
                    rowData={validationResult.offers}
                    onGridReady={(params) => {
                      offersGridRef.current = params.api;
                      params.api.sizeColumnsToFit();
                    }}
                    defaultColDef={{
                      flex: 1,
                      autoHeight: true,
                      resizable: true,
                    }}
                    rowClass="border-t border-dashed"
                    suppressRowClickSelection
                    animateRows
                  />
                </div>
              </div>
            )}

            {/* Employee Exercise Documents */}
            {isPreviewMode && employeesForLetters.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    Employee Exercise Documents
                  </h3>
                  <span className="text-xs text-gray-400">
                    {employeesForLetters.length} employee
                    {employeesForLetters.length !== 1 ? "s" : ""}
                  </span>
                </div>

                {isLoadingEmployees && (
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                    <CircularProgress size={14} />
                    <span>Resolving employee data…</span>
                  </div>
                )}

                <div className="flex items-center gap-4 mb-4">
                  <div className="flex-1">
                    <p className="text-sm text-gray-600">
                      Upload one exercise letter per employee. You can select
                      multiple files at once — filenames are fuzzy-matched to
                      the employee&apos;s HR ID (e.g.{" "}
                      <span className="font-mono bg-gray-100 px-1 rounded">
                        EMP001.pdf
                      </span>
                      ).
                    </p>
                  </div>
                  <div>
                    <ButtonPrimary
                      onClick={() => bulkInputRef.current?.click()}
                    >
                      Bulk Upload Letters
                    </ButtonPrimary>
                    <input
                      type="file"
                      multiple
                      accept=".pdf"
                      className="hidden"
                      ref={bulkInputRef}
                      onChange={(e) => {
                        if (e.target.files?.length)
                          handleBulkSelect(e.target.files);
                        e.target.value = "";
                      }}
                    />
                  </div>
                </div>

                <div className="border border-gray-200 rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left px-4 py-2.5 font-medium text-gray-600 w-1/3">
                          Employee
                        </th>
                        <th className="text-left px-4 py-2.5 font-medium text-gray-600 w-1/6">
                          HR ID
                        </th>
                        <th className="text-left px-4 py-2.5 font-medium text-gray-600 w-1/6">
                          Grants Exercised
                        </th>
                        <th className="text-left px-4 py-2.5 font-medium text-gray-600">
                          Exercise Letter
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {employeesForLetters.map((emp) => {
                        const uploaded = uploadedLetters[emp.hrId];
                        const resolved =
                          !isLoadingEmployees && employeeMap?.has(emp.hrId);
                        return (
                          <tr
                            key={emp.hrId}
                            className="border-b border-gray-100 last:border-0"
                          >
                            <td className="px-4 py-2.5 font-medium text-gray-700">
                              {emp.displayName}
                            </td>
                            <td className="px-4 py-2.5">
                              <span className="font-mono text-xs text-gray-600">
                                {emp.hrId}
                              </span>
                              {!isLoadingEmployees && !resolved && (
                                <span
                                  className="ml-2 text-xs text-amber-600"
                                  title="HR ID could not be matched to a known employee"
                                >
                                  unresolved
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2.5 text-gray-600">
                              {emp.grantsCount}
                            </td>
                            <td className="px-4 py-2">
                              {uploaded ? (
                                <div className="flex items-center gap-2">
                                  <Icon
                                    icon="material-symbols:check-circle-outline"
                                    className="text-green-500 shrink-0"
                                    height={16}
                                  />
                                  <span
                                    className="text-xs text-gray-600 truncate max-w-[180px]"
                                    title={uploaded.file.name}
                                  >
                                    {uploaded.file.name}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => handleLetterRemove(emp.hrId)}
                                    className="text-gray-400 hover:text-red-500 ml-1 shrink-0"
                                    title="Remove"
                                  >
                                    <Icon icon="mdi:close" height={14} />
                                  </button>
                                </div>
                              ) : (
                                <label className="cursor-pointer text-sm text-black underline font-medium hover:text-gray-700">
                                  Upload
                                  <input
                                    type="file"
                                    accept=".pdf"
                                    className="hidden"
                                    onChange={(e) => {
                                      const f = e.target.files?.[0];
                                      if (f) handleLetterUpload(emp.hrId, f);
                                      e.target.value = "";
                                    }}
                                  />
                                </label>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Action buttons after validation */}
            <HStack className="justify-end gap-3 pt-2 border-t border-gray-100">
              <ButtonPrimary1 onClick={handleClear}>
                Upload Different File
              </ButtonPrimary1>
              {isPreviewMode ? (
                <ButtonPrimary onClick={handleConfirm} disabled={isConfirming}>
                  {isConfirming ? (
                    <HStack className="items-center gap-2">
                      <Icon
                        icon="lucide:loader-2"
                        className="animate-spin"
                        height={16}
                      />
                      {isUploadingLetters ? "Uploading…" : "Importing..."}
                    </HStack>
                  ) : (
                    "Confirm & Import"
                  )}
                </ButtonPrimary>
              ) : (
                <ButtonPrimary onClick={handleValidate} disabled={isValidating}>
                  {isValidating ? (
                    <HStack className="items-center gap-2">
                      <Icon
                        icon="lucide:loader-2"
                        className="animate-spin"
                        height={16}
                      />
                      Validating...
                    </HStack>
                  ) : (
                    "Validate"
                  )}
                </ButtonPrimary>
              )}
            </HStack>
          </>
        )}

        {/* Instructions (shown before any upload) */}
        {!hasValidationResult && !isValidating && (
          <div className="w-full max-w-2xl mx-auto">
            <Instructions
              data={[
                "The file must have two sheets — Sheet 1 for event details, Sheet 2 for employee offers",
                "Sheet 1 defines the exercise event (name, dates, type, etc.) — only one event per file",
                "Sheet 2 lists the exercise offers — each row is one offer for a grant under that event",
                "Grant Identifier in Sheet 2 must match an existing grant in the system",
                "Options exercised must not exceed net vested options for the grant",
                "Dates must be in DD/MM/YYYY format",
                "You can upload exercise letters for each employee directly on this page after validation.",
              ]}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default HistoricExerciseImport;
